import { scrollIntoView } from '@ta-interaktiv/utils'
import { useMFContext } from 'components/MFHelpers'
import { scaleLinear } from 'd3'

import {
  useEffect,
  useState,
  memo,
  ReactNode,
  useMemo,
  useRef,
  useCallback,
} from 'react'
import styled from 'styled-components'

// Column interface defines the structure for table columns
export interface Column {
  key: string
  label: string | ReactNode
  width?: string
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  valuesAsBars?: boolean
  barMax?: number
  barColor?: string
  format?: (value: any) => string | number | ReactNode
  cellStyle?: (
    value: any,
    rowData: any,
    rowNumber: number
  ) => React.CSSProperties | undefined
}

// DataTable component for displaying sortable, searchable tabular data
const DataTable = ({
  selected,
  data,
  columns,
  defaultSort,
  defaultRowCount = 12,
  hideSearch = false,
  showAllRows = true,
  alwaysFirst,
}: {
  data: any[]
  selected?: { prop: string; value: any } | undefined
  columns: Column[]
  defaultSort?: {
    key: string
    direction: 'asc' | 'desc'
  }
  defaultRowCount?: number | null
  hideSearch?: boolean
  showAllRows?: boolean
  alwaysFirst?: { key: string; value: any }
}) => {
  const { _t, isMobile, currentLanguage } = useMFContext()
  const tableref = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [searchString, setSearchString] = useState('')
  const [prevSelected, setPrevSelected] = useState<
    { prop: string; value: any } | undefined
  >(undefined)
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: 'asc' | 'desc'
  }>(
    defaultSort ? { ...defaultSort } : { key: columns[0].key, direction: 'asc' }
  )

  const [showAll, setShowAll] = useState(!defaultRowCount || showAllRows)
  // const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    if (selected !== prevSelected) {
      setPrevSelected(selected)
    }
  }, [selected])

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
    }
    setSortConfig({ key, direction })
  }

  // Filter data based on search string
  const filteredData = useMemo(() => {
    if (!searchString.trim()) return data
    return data.filter((row) => {
      return columns.some((column) => {
        const value = row[column.key]
        if (value == null) return false
        return String(value).toLowerCase().includes(searchString.toLowerCase())
      })
    })
  }, [data, searchString, columns])

  // Sort filtered data based on current sort configuration
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      // If alwaysFirst is provided, check if either a or b matches the condition
      if (alwaysFirst) {
        const aIsFirst = a[alwaysFirst.key] === alwaysFirst.value
        const bIsFirst = b[alwaysFirst.key] === alwaysFirst.value

        if (aIsFirst && !bIsFirst) return -1
        if (!aIsFirst && bIsFirst) return 1
      }

      // Regular sorting logic
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1
      }
      return 0
    })
    return sorted
  }, [filteredData, sortConfig, alwaysFirst])

  // Determine which rows to display based on defaultRowCount and selected row
  const displayedData = useMemo(() => {
    if (!defaultRowCount || showAll) {
      return sortedData
    }
    const selectedRowIndex = sortedData.findIndex(
      (row) => selected && row[selected.prop] === selected.value
    )
    if (selectedRowIndex === -1) {
      return sortedData.slice(0, defaultRowCount)
    }
    return sortedData.slice(0, Math.max(selectedRowIndex + 1, defaultRowCount))
  }, [sortedData, showAll, defaultRowCount, selected])

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <TableHeadWrapper>
        {!hideSearch && (
          <SearchRow>
            <SearchInput
              type='text'
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder={_t('search_placeholder') + ''}
            />
            {searchString !== '' && (
              <ClearButton onClick={() => setSearchString('')}>×</ClearButton>
            )}
          </SearchRow>
        )}
      </TableHeadWrapper>
      <TableHead $needsScroll={true}>
        <Table>
          <thead>
            <TableHeaderRow>
              {columns.map((column) => (
                <TableHeader
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{
                    flex: column.width ? 'none' : 1,
                    minWidth: column.width,
                    width: column.width,
                    textAlign: column.align || 'left',
                  }}
                  sortable={column.sortable?.toString()}
                >
                  {column.label}
                  {column.sortable && sortConfig.key === column.key && (
                    <SortIndicator>
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </SortIndicator>
                  )}
                </TableHeader>
              ))}
              {!isMobile && <th style={{ width: '16px' }} />}
            </TableHeaderRow>
          </thead>
        </Table>
      </TableHead>
      <GradientWrapper>
        <DataTableTestContainer
          ref={containerRef}
          style={{
            maxHeight:
              defaultRowCount && !showAll
                ? 'auto'
                : 32 * (isMobile ? defaultRowCount : defaultRowCount),
            height:
              defaultRowCount && !showAll
                ? 'auto'
                : 32 * (isMobile ? defaultRowCount : defaultRowCount),
          }}
        >
          {displayedData.map((row, index) => {
            return (
              <DataRow
                key={`${row.id || index}`}
                columns={columns}
                index={index}
                row={row}
                isSelected={
                  selected ? row[selected.prop] === selected.value : false
                }
                isFixed={
                  alwaysFirst
                    ? row[alwaysFirst.key] === alwaysFirst.value
                    : false
                }
                containerRef={containerRef}
              />
            )
          })}
          <div ref={tableref} />
        </DataTableTestContainer>
        {defaultRowCount && !showAllRows && (
          <div>
            <button
              onClick={() => {
                if (tableref.current) {
                  scrollIntoView(tableref.current)
                }
                setShowAll(!showAll)
              }}
            >
              {showAll ? _t('showless') : _t('showall')}
            </button>
          </div>
        )}
        {defaultRowCount < data.length && (
          <BottomGradient $selected={selected ? true : false} />
        )}
        <TopGradient $selected={selected ? true : false} />
      </GradientWrapper>
    </div>
  )
}

// DataRow component handles individual row rendering and sticky positioning
const DataRow = memo(
  ({
    row,
    isSelected,
    columns,
    index,
    containerRef,
    isFixed,
  }: {
    row: any
    isSelected: boolean
    columns: Column[]
    index: number
    containerRef: React.RefObject<HTMLDivElement>
    isFixed: boolean
  }) => {
    const [rowPos, setRowPos] = useState<'top' | 'bottom' | null>(null)
    const rowRef = useRef<HTMLDivElement>(null)

    // Update row's sticky position based on scroll position
    const updateStickyPosition = useCallback(() => {
      if (!isSelected || !rowRef.current || !containerRef.current) return

      const rowRect = rowRef.current.getBoundingClientRect()
      const containerRect = containerRef.current.getBoundingClientRect()
      const containerHeight = containerRef.current.offsetHeight
      const rowOffsetTop = rowRect.top - containerRect.top

      const threshold = 1

      if (rowOffsetTop <= threshold) {
        setRowPos('top')
      } else if (rowOffsetTop + rowRect.height >= containerHeight - threshold) {
        setRowPos('bottom')
      } else {
        setRowPos(null)
      }
    }, [isSelected])

    useEffect(() => {
      if (!isSelected) {
        setRowPos(null)
        return
      }

      const container = containerRef.current
      if (!container) return

      // Initial position check
      requestAnimationFrame(updateStickyPosition)

      let rafId: number
      const handleScroll = () => {
        cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(updateStickyPosition)
      }

      container.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        container.removeEventListener('scroll', handleScroll)
        cancelAnimationFrame(rafId)
      }
    }, [isSelected, updateStickyPosition])

    return (
      <Row
        ref={rowRef}
        $isSelected={isSelected}
        $stickyPos={rowPos}
        style={{
          backgroundColor: isSelected
            ? 'var(--data-table-selected-bg)'
            : index % 2 === 0
            ? 'var(--tablerow-bg)'
            : 'var(--site-background)',
          color: isSelected
            ? 'var(--data-table-selected-text)'
            : 'var(--text-color)',
          position: isFixed ? 'sticky' : undefined,
          top: isFixed ? '0' : undefined,
          zIndex: isSelected ? 9999 : 0,
          borderBottom: isFixed ? '2px solid var(--gray-2)' : 'none',
          borderTop: isFixed ? '2px solid var(--gray-2)' : 'none',
          fontWeight: isFixed ? 'bold' : 'normal',
        }}
      >
        {columns.map((column): JSX.Element => {
          const value = row[column.key]
          const formattedValue = column.format ? column.format(value) : value
          const barScale = scaleLinear<number>()
            .domain([0, column.barMax || 100])
            .range([0, 100])

          return (
            <DataCell
              key={column.key}
              style={{
                fontWeight: isSelected ? 'bold' : 'normal',
                flex: column.width ? 'none' : 1,
                width: column.width,
                minWidth: column.width,
                textAlign: column.align || 'left',
                ...column.cellStyle?.(value, row, index),
              }}
            >
              {column.valuesAsBars ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <BarContainer $isSelected={isSelected}>
                    <Bar
                      $width={barScale(Number(value))}
                      style={{ backgroundColor: column.barColor }}
                    >
                      {value >= 20 && <BarValue>{formattedValue}</BarValue>}
                    </Bar>
                    {value < 20 && (
                      <BarValue style={{ whiteSpace: 'nowrap' }}>
                        {formattedValue}
                      </BarValue>
                    )}
                  </BarContainer>
                </div>
              ) : (
                formattedValue
              )}
            </DataCell>
          )
        })}
      </Row>
    )
  }
)
const BarValue = styled.div`
  position: absolute;
  right: 4px;
  top: 0;
  white-space: nowrap;
  font-size: 12px;
  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`
// Styled Components for Table Layout and Visual Effects
const GradientWrapper = styled.div`
  position: relative;
`

// Gradient overlay for bottom fade effect
const BottomGradient = styled.div<{ $selected: boolean }>`
  height: 16px;
  background: linear-gradient(to top, var(--text-color) 0%, transparent 90%);
  position: absolute;
  bottom: ${(props) => (props.$selected ? '8px' : '0')};
  opacity: 0.1;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  z-index: 1;
`

// Gradient overlay for top fade effect
const TopGradient = styled.div<{ $selected: boolean }>`
  height: 16px;
  background: linear-gradient(to bottom, var(--text-color) 0%, transparent 90%);
  position: absolute;
  top: ${(props) => (props.$selected ? '0px' : '0')};
  left: 50%;
  opacity: 0.1;
  transform: translateX(-50%);
  width: 100%;
  z-index: 1;
`

// Clear search input button
const ClearButton = styled.button`
  margin-left: 8px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--gray-3);
  font-size: 22px;
  position: absolute;
  right: 0;
  top: 0;
  padding-left: 8px;
  padding-right: 8px;
  max-height: 39px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-left: 1px solid var(--gray-4);
  cursor: pointer;
  &:hover {
    background-color: var(--gray-4);
  }
`

// Search input container
const SearchRow = styled.div`
  padding-bottom: 8px;
  display: flex;
  position: relative;
  justify-content: flex-end;
  width: 100%;
  margin-top: 15px;
  button {
    margin-left: 8px;
    height: 100% !important;
    align-self: center;
  }
`

// Search input field
const SearchInput = styled.input`
  font-size: 16px;
  padding: 8px;
  border: 1px solid var(--gray-4);
  border-radius: 4px;
  height: 40px;
  padding-right: 40px;
  @media (max-width: 711px) {
    width: 100%;
  }
`

// Main table container with scroll functionality
const DataTableTestContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  width: 100%;
  display: inline-block;
  /* isolation: isolate; */
`

// Table row with sticky positioning support
const Row = styled.div<{
  $isSelected: boolean
  $stickyPos: 'top' | 'bottom' | null
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 32px;
  position: ${(props) =>
    props.$isSelected && props.$stickyPos ? 'sticky' : 'relative'};
  top: ${(props) =>
    props.$isSelected && props.$stickyPos === 'top' ? '0' : 'auto'};
  bottom: ${(props) =>
    props.$isSelected && props.$stickyPos === 'bottom' ? '0' : 'auto'};
z-index: ${(props) => (props.$isSelected ? 9999 : 0)};
  line-height: 1.2em;
  font-size: 13px;
  border-bottom: ${(props) =>
    props.$isSelected ? '1px solid var(--site-background)' : 'none'};
  border-top: ${(props) =>
    props.$isSelected ? '1px solid var(--site-background)' : 'none'};
  @media (max-width: 768px) {
    font-size: 12px;
  }
`

// Table header with sort functionality
const TableHeader = styled.th<{ sortable?: string }>`
  position: sticky;
  top: 0;
  padding: 8px;
  line-height: 1em;
  cursor: ${(props) => (props.sortable === 'true' ? 'pointer' : 'default')};
  white-space: nowrap;
  z-index: 1;
  @media (min-width: 711px) {
    &:hover {
      background-color: var(--table-head-hover);
    }
  }
`

const TableHeadWrapper = styled.div`
  width: 100%;
`

const TableHead = styled.div<{ $needsScroll?: boolean }>`
  width: 100%;
  position: relative;
  overflow-x: hidden;
  margin-bottom: 2px;
`

const Table = styled.table`
  overflow-x: hidden;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 13px;
  @media (max-width: 768px) {
    width: calc(100% - 40px);
    font-size: 13px;
  }
`

const SortIndicator = styled.span`
  margin-left: 4px;
  font-size: 14px;
  line-height: 0.8em;
  margin-top: -6px;
  font-weight: 900;
`

const TableHeaderRow = styled.tr`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 14px;
  white-space: break-spaces;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const DataCell = styled.div`
  padding: 8px;
  border-right: 1px solid var(--data-table-border);
`

// Bar component for visualizing values
const BarContainer = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  height: 16px;
  background-color: ${(props) =>
    props.$isSelected
      ? 'var(--data-table-selected-bar-bg)'
      : 'var(--data-table-bar-bg)'};
  color: ${(props) =>
    props.$isSelected
      ? 'var(--data-table-selected-bar-text)'
      : 'var(--data-table-bar-text)'};
  position: relative;
  border-radius: 2px;
  overflow: hidden;
`

const Bar = styled.div<{ $width: number }>`
  height: 100%;
  width: ${(props) => `${props.$width}%`};
  background-color: var(--brandblue-main);
  border-radius: 2px;
  transition: width 0.3s ease;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  color: var(--gray-1);
  font-size: 12px;
  font-weight: 500;
  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`

export default DataTable
